import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Eye, EyeOff, ArrowLeft, Check } from 'lucide-react';

const SignupPage = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  const { signup } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const redirectPath = searchParams.get('redirect');
  const purchaseEvent = searchParams.get('purchase');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    // Require a plausible phone number (>=10 digits) so we can text updates
    const phoneDigits = (phone || '').replace(/\D/g, '');
    if (phoneDigits.length < 10) {
      setError('Please enter a valid phone number (at least 10 digits)');
      return;
    }

    setLoading(true);

    try {
      await signup(email, password, name, phone);
      
      // Handle redirect with purchase intent
      if (redirectPath) {
        const redirectUrl = purchaseEvent 
          ? `${redirectPath}?purchase=${purchaseEvent}`
          : redirectPath;
        navigate(redirectUrl);
        return;
      }
      
      // Brand-new accounts land on the Netflix home. The in-place lock
      // modal there offers Monthly/Annual plans without leaving `/`.
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create account. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const trialBenefits = [
    'Full community access',
    'AI Training Assistant',
    'Pay-per-photo & video event access',
    'Subscribe anytime from your profile'
  ];

  return (
    <div className="min-h-screen flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-8 bg-background">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <Link to="/" className="flex items-center justify-center gap-3 mb-8 group">
          <img 
            src="https://customer-assets.emergentagent.com/job_arena-coach-1/artifacts/zh1vb354_Elevated%20Logo.png" 
            alt="Elevated Roping Media" 
            className="h-12 w-auto"
          />
          <div className="flex flex-col">
            <span className="font-display font-bold text-sm tracking-tight text-foreground group-hover:text-primary transition-colors leading-none">
              ELEVATED
            </span>
            <span className="font-display font-bold text-xs tracking-wider text-muted-foreground leading-none">
              ROPING MEDIA
            </span>
          </div>
        </Link>
      </div>

      <Card className="sm:mx-auto sm:w-full sm:max-w-md">
        <CardHeader className="space-y-1">
          <CardTitle className="font-display text-2xl uppercase tracking-wide text-center">
            Create Your Account
          </CardTitle>
          <CardDescription className="text-center">
            Create your account and start improving today
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Trial Benefits */}
          <div className="bg-secondary/10 rounded-lg p-4 mb-6">
            <p className="font-semibold text-sm text-secondary mb-2">What's included:</p>
            <ul className="space-y-1">
              {trialBenefits.map((benefit, i) => (
                <li key={i} className="flex items-center gap-2 text-sm">
                  <Check className="w-4 h-4 text-secondary" />
                  <span>{benefit}</span>
                </li>
              ))}
            </ul>
          </div>

          {error && (
            <Alert variant="destructive" className="mb-6">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                type="text"
                placeholder="Your name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="h-12"
                data-testid="signup-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="h-12"
                data-testid="signup-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">Phone Number</Label>
              <Input
                id="phone"
                type="tel"
                placeholder="(403) 555-1234"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                required
                autoComplete="tel"
                className="h-12"
                data-testid="signup-phone-input"
              />
              <p className="text-xs text-muted-foreground">
                We text critique updates, purchase receipts, and event ticket links to this number.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="At least 6 characters"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="h-12 pr-10"
                  data-testid="signup-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <Input
                id="confirmPassword"
                type={showPassword ? 'text' : 'password'}
                placeholder="Confirm your password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                className="h-12"
                data-testid="signup-confirm-password-input"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-primary hover:bg-primary/90 text-primary-foreground rounded-full font-bold uppercase tracking-wide"
              data-testid="signup-submit-btn"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="spinner" />
                  Creating Account...
                </span>
              ) : (
                'Create Account'
              )}
            </Button>
          </form>

          <p className="mt-4 text-xs text-muted-foreground text-center">
            By signing up, you agree to our terms of service and privacy policy.
          </p>

          <div className="mt-6 text-center">
            <p className="text-muted-foreground">
              Already have an account?{' '}
              <Link to="/login" className="text-primary font-semibold hover:underline">
                Sign in
              </Link>
            </p>
          </div>
        </CardContent>
      </Card>

      <div className="mt-8 text-center">
        <Link to="/" className="text-muted-foreground hover:text-foreground inline-flex items-center gap-2">
          <ArrowLeft className="w-4 h-4" />
          Back to home
        </Link>
      </div>
    </div>
  );
};

export default SignupPage;
